import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Users, UserPlus, Copy, Check, Trash2, ShieldCheck, Mail, Link2, RotateCw, AlertOctagon,
} from "lucide-react";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin", hint: "Full control" },
  { value: "member", label: "Member", hint: "Create + edit data" },
  { value: "viewer", label: "Viewer", hint: "Read-only" },
];

export default function Members() {
  const { workspace, user, refresh, switchWorkspace } = useAuth();
  const nav = useNavigate();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [creating, setCreating] = useState(false);
  const [copiedInvite, setCopiedInvite] = useState(null);
  const [showDanger, setShowDanger] = useState(false);
  const [dangerConfirm, setDangerConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [seats, setSeats] = useState(null);

  const loadSeats = () => api.get("/workspace/seats").then((r) => setSeats(r.data)).catch(() => {});
  useEffect(() => { loadSeats(); }, []);

  const wsId = workspace?.id;

  const load = async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const [m, i] = await Promise.all([
        api.get(`/workspaces/${wsId}/members`),
        api.get(`/workspaces/${wsId}/invites`),
      ]);
      setMembers(m.data);
      setInvites(i.data.filter((inv) => !inv.accepted_at && !inv.revoked));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load members");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [wsId]);

  const changeRole = async (uid, newRole) => {
    try {
      await api.patch(`/workspaces/${wsId}/members/${uid}`, { role: newRole });
      toast.success("Role updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update role");
    }
  };

  const removeMember = async (uid) => {
    if (!window.confirm("Remove this member? They lose access to this workspace immediately.")) return;
    try {
      await api.delete(`/workspaces/${wsId}/members/${uid}`);
      toast.success("Member removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove");
    }
  };

  const createInvite = async (e) => {
    e.preventDefault();
    if (creating) return;
    setCreating(true);
    try {
      const body = { role: inviteRole };
      if (inviteEmail.trim()) body.email = inviteEmail.trim();
      const { data } = await api.post(`/workspaces/${wsId}/invites`, body);
      toast.success(data.email_sent
        ? `Invite sent to ${data.email}`
        : (data.email ? "Invite created — email delivery not configured" : "Share-link invite ready"));
      setInviteEmail(""); setInviteRole("member");
      setShowInvite(false);
      // Auto-copy share link so the admin can paste it right away.
      try { await navigator.clipboard.writeText(data.link); setCopiedInvite(data.id); setTimeout(() => setCopiedInvite(null), 2500); } catch { /* clipboard unavailable */ }
      load(); loadSeats();
    } catch (err) {
      // Iteration 46 — surface seat-limit error nicely.
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429 && detail?.error === "seat_limit_reached") {
        toast.error(detail.upgrade_message);
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to create invite");
      }
    } finally { setCreating(false); }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm("Revoke this invitation?")) return;
    try {
      await api.delete(`/invites/${id}`);
      toast.success("Invitation revoked");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revoke");
    }
  };

  const copyLink = async (inv) => {
    try {
      await navigator.clipboard.writeText(inv.link);
      setCopiedInvite(inv.id);
      setTimeout(() => setCopiedInvite(null), 2500);
    } catch { toast.error("Clipboard unavailable"); }
  };

  const deleteWorkspace = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/workspaces/${wsId}`);
      toast.success("Workspace deleted");
      // CRITICAL: mint a JWT for the fallback workspace BEFORE calling refresh()
      // — otherwise `refresh()` runs GET /auth/me with the (now-invalid) old
      // token, hits 403, clears the user, and ProtectedRoute yanks us to /login
      // before the switch call completes.
      if (data.next_workspace_id) {
        try {
          await switchWorkspace(data.next_workspace_id);
        } catch {
          // Fall back to refresh so state at least reflects reality.
          await refresh();
        }
        nav("/app");
      } else {
        // No workspaces left — force logout.
        localStorage.removeItem("cb_token");
        try { await refresh(); } catch { /* ignore */ }
        nav("/login");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete workspace");
    } finally { setDeleting(false); setShowDanger(false); setDangerConfirm(""); }
  };

  return (
    <div className="p-8 max-w-4xl" data-testid="members-page">
      <header className="mb-8 flex items-start justify-between gap-4">
        <div>
          <p className="eyebrow mb-3">Members</p>
          <h1 className="font-display text-3xl">Access & roles</h1>
          <p className="mt-2 text-muted-foreground">
            Invite teammates and control what they can do inside <span className="font-medium">{workspace?.name}</span>.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {seats && seats.limit != null && (
            <div className="text-right" data-testid="members-seats-meter">
              <p className={`text-xs font-mono ${seats.at_limit ? "text-red-500" : seats.near_limit ? "text-amber-500" : "text-muted-foreground"}`}>
                {seats.used} of {seats.limit} seats used
              </p>
              <div className="w-32 h-1.5 mt-1 rounded-full bg-secondary overflow-hidden">
                <div className={`h-full ${seats.at_limit ? "bg-red-500" : seats.near_limit ? "bg-amber-500" : "bg-emerald-500"}`}
                  style={{ width: `${Math.min(100, (seats.used / seats.limit) * 100)}%` }} />
              </div>
            </div>
          )}
          <Button
            onClick={() => setShowInvite(true)}
            disabled={seats?.at_limit}
            className="rounded-full"
            data-testid="members-invite-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Invite
          </Button>
        </div>
      </header>
      {seats?.at_limit && (
        <div className="mb-6 rounded-md border border-red-500/40 bg-red-500/5 px-4 py-3 text-sm text-red-500 flex items-center justify-between"
          data-testid="members-seats-atlimit-banner">
          <span>Seat limit reached. Upgrade your plan to invite more members.</span>
          <Link to="/app/billing" className="underline hover:no-underline font-medium">Upgrade →</Link>
        </div>
      )}
      {seats?.near_limit && !seats.at_limit && (
        <div className="mb-6 rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm text-amber-500"
          data-testid="members-seats-nearlimit-banner">
          {seats.used} of {seats.limit} seats used — upgrade soon.
        </div>
      )}

      <section className="mb-10">
        <h2 className="font-medium mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Members ({members.length})</h2>
        {loading ? (
          <div className="card-flat p-6 text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="card-flat divide-y divide-border/60">
            {members.map((m) => (
              <div key={m.user_id} className="p-4 flex items-center gap-4" data-testid={`member-row-${m.user_id}`}>
                <div className="w-9 h-9 rounded-full bg-foreground text-background flex items-center justify-center text-sm font-semibold shrink-0">
                  {(m.name || m.email || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {m.name || m.email}
                    {m.user_id === user?.id && <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">you</span>}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                </div>
                <select
                  value={m.role}
                  onChange={(e) => changeRole(m.user_id, e.target.value)}
                  disabled={m.user_id === user?.id}
                  className="rounded-md border border-border/60 bg-background px-2 py-1 text-xs"
                  data-testid={`member-role-${m.user_id}`}
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <button
                  onClick={() => removeMember(m.user_id)}
                  disabled={m.user_id === user?.id}
                  className="text-muted-foreground hover:text-destructive disabled:opacity-30 disabled:cursor-not-allowed"
                  title={m.user_id === user?.id ? "You cannot remove yourself" : "Remove member"}
                  data-testid={`member-remove-${m.user_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-medium mb-3 flex items-center gap-2"><Mail className="w-4 h-4" /> Pending invitations ({invites.length})</h2>
        {invites.length === 0 ? (
          <div className="card-flat p-6 text-sm text-muted-foreground" data-testid="invites-empty">
            No pending invitations. Create one to bring a teammate in.
          </div>
        ) : (
          <div className="card-flat divide-y divide-border/60">
            {invites.map((inv) => (
              <div key={inv.id} className="p-4 flex items-center gap-4" data-testid={`invite-row-${inv.id}`}>
                {inv.email ? <Mail className="w-4 h-4 text-muted-foreground" /> : <Link2 className="w-4 h-4 text-muted-foreground" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{inv.email || "Shareable link"}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Role: {inv.role} · expires {new Date(inv.expires_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => copyLink(inv)}
                  className="text-muted-foreground hover:text-foreground text-xs flex items-center gap-1 border border-border/60 rounded-full px-3 py-1"
                  data-testid={`invite-copy-${inv.id}`}
                >
                  {copiedInvite === inv.id
                    ? <><Check className="w-3 h-3" /> Copied</>
                    : <><Copy className="w-3 h-3" /> Copy link</>}
                </button>
                <button
                  onClick={() => revokeInvite(inv.id)}
                  className="text-muted-foreground hover:text-destructive"
                  title="Revoke"
                  data-testid={`invite-revoke-${inv.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-14" data-testid="danger-zone-section">
        <h2 className="font-medium mb-3 flex items-center gap-2 text-destructive">
          <AlertOctagon className="w-4 h-4" /> Danger zone
        </h2>
        <div className="card-flat p-5 border-destructive/40 flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[240px]">
            <p className="text-sm font-medium">Delete this workspace</p>
            <p className="text-xs text-muted-foreground mt-1">
              Removes <span className="font-medium">{workspace?.name}</span> and every source, knowledge entry, chat message, API key, integration, and invitation inside it. This cannot be undone.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowDanger(true)}
            className="rounded-full border-destructive/60 text-destructive hover:bg-destructive hover:text-destructive-foreground"
            data-testid="delete-workspace-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete workspace
          </Button>
        </div>
      </section>

      <Dialog open={showInvite} onOpenChange={setShowInvite}><DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Invite a teammate</DialogTitle>
            <DialogDescription>
              Send an email or generate a shareable link. New joiners get the role you pick here.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createInvite} className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="invite-email">Email (optional)</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="teammate@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                data-testid="invite-email-input"
              />
              <p className="text-[10px] text-muted-foreground">Leave empty to create a shareable link that anyone can use.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="invite-role">Role</Label>
              <select
                id="invite-role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="w-full rounded-md border border-border/60 bg-background px-3 py-2 text-sm"
                data-testid="invite-role-select"
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label} — {r.hint}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="ghost" onClick={() => setShowInvite(false)} className="rounded-full">Cancel</Button>
              <Button type="submit" disabled={creating} className="rounded-full" data-testid="invite-submit-btn">
                {creating ? <><RotateCw className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : "Create invitation"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showDanger} onOpenChange={(o) => { setShowDanger(o); if (!o) setDangerConfirm(""); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-destructive flex items-center gap-2">
              <AlertOctagon className="w-5 h-5" /> Delete workspace
            </DialogTitle>
            <DialogDescription>
              This will permanently remove <strong>{workspace?.name}</strong> and every source, knowledge entry, chat message, agent key, integration, and pending invitation inside it. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <Label htmlFor="danger-confirm" className="text-xs">
              Type <span className="font-mono text-destructive">{workspace?.name}</span> to confirm
            </Label>
            <Input
              id="danger-confirm"
              value={dangerConfirm}
              onChange={(e) => setDangerConfirm(e.target.value)}
              placeholder={workspace?.name}
              data-testid="delete-workspace-confirm-input"
              autoComplete="off"
            />
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="ghost" onClick={() => { setShowDanger(false); setDangerConfirm(""); }} className="rounded-full">Cancel</Button>
              <Button
                variant="destructive"
                disabled={dangerConfirm !== workspace?.name || deleting}
                onClick={deleteWorkspace}
                className="rounded-full"
                data-testid="delete-workspace-confirm-btn"
              >
                {deleting ? <><RotateCw className="w-4 h-4 mr-2 animate-spin" /> Deleting…</> : "Delete forever"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
